// Step-up biometric gate (client half). The REAL enforcement is Firestore
// rules + the WebAuthn Cloud Functions — this module just runs the ceremony
// (via the vendored SimpleWebAuthn browser bundle) and shows the gate UI.
// Face ID / Touch ID / device PIN via platform passkeys; enrolment on first
// use counts as the first verification (one tap).
import { getFunctions, httpsCallable } from './fb/functions.js';
import { doc, getDoc } from './fb/firestore.js';
import { app, auth, db } from './firebase.js';
import { ORG_ID } from './config.js';
import { escapeHtml } from './ui.js';

const fns = getFunctions(app, 'europe-west2');
const call = (name, data) => httpsCallable(fns, name)(data).then((r) => r.data);
const WINDOW_MS = 15 * 60 * 1000;

function deviceName() {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows PC';
  if (/Android/.test(ua)) return 'Android';
  return 'Device';
}

/** Freshness check against the server-written session doc (own-read allowed). */
export async function stepUpFresh() {
  const uid = auth.currentUser && auth.currentUser.uid;
  if (!uid) return false;
  try {
    const snap = await getDoc(doc(db, `organisations/${ORG_ID}/financeSessions/${uid}`));
    if (!snap.exists()) return false;
    const t = snap.data().verifiedAt;
    const when = t && typeof t.toDate === 'function' ? t.toDate().getTime() : 0;
    return Date.now() - when < WINDOW_MS - 30 * 1000; // 30s safety margin
  } catch (e) { return false; }
}

async function runCeremony(statusEl) {
  const swb = window.SimpleWebAuthnBrowser;
  if (!swb || !swb.browserSupportsWebAuthn()) {
    throw new Error('This browser has no passkey support — use Safari/Chrome on your Mac or iPhone.');
  }
  statusEl.textContent = 'Preparing…';
  let options = await call('webauthnAuthOptions');
  if (options.enrol) {
    statusEl.textContent = 'First time: creating your passkey — approve the Face ID / Touch ID prompt.';
    const regOptions = await call('webauthnRegisterOptions');
    const attestation = await swb.startRegistration(regOptions);
    await call('webauthnRegisterVerify', { attestation, deviceName: deviceName() });
    return; // enrolment counts as the first verification
  }
  statusEl.textContent = 'Approve the Face ID / Touch ID prompt…';
  const assertion = await swb.startAuthentication(options);
  await call('webauthnAuthVerify', { assertion });
}

/**
 * Gate helper: resolves true once a fresh verification exists; shows the
 * verify overlay otherwise. Returns false only if the user dismisses it.
 */
export function ensureStepUp(opts = {}) {
  return new Promise(async (resolve) => {
    // force:true (Bank, Karan 10/07) always demands a fresh biometric — closing
    // and reopening the page re-verifies, never riding the 15-min window.
    if (!opts.force && await stepUpFresh()) { resolve(true); return; }

    // v4 (design p49): a compact, left-aligned confirm over a BLURRED app — the
    // one dialog that blurs, because it hides the figures behind it. The design
    // said "on this device only"; the fresh check is held per person on the
    // server, so the copy says what's true.
    const prev = document.activeElement;
    const scrim = document.createElement('div');
    scrim.className = 'su4-scrim';
    const title = opts.title || (opts.force ? 'Unlock the bank' : 'Unlock pay and cost rates');
    scrim.innerHTML = `
      <div class="su4" role="dialog" aria-modal="true" aria-labelledby="su4-title">
        <span class="su4-icon" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 11h12v9.5H6z M8.5 11V8a3.5 3.5 0 0 1 7 0v3"/></svg></span>
        <h2 id="su4-title">${title.replace(/[<>&]/g, '')}</h2>
        <p class="su4-body">Use Face ID, Touch ID or this device’s PIN. Bank, pay and cost rates stay unlocked for 15 minutes.</p>
        <p class="su4-note" id="su-status">First time on this device? You’ll be asked to register it.</p>
        <p class="su4-err" id="su-err"></p>
        <div class="su4-btns">
          <button class="btn lg" id="su-cancel" type="button">Not now</button>
          <button class="btn lg primary" id="su-go" type="button">Unlock</button>
        </div>
      </div>`;
    document.body.appendChild(scrim);
    const content = scrim;
    const onKey = (ev) => { if (ev.key === 'Escape') done(false); };
    document.addEventListener('keydown', onKey);
    const close = () => { scrim.remove(); document.removeEventListener('keydown', onKey); if (prev && prev.focus) prev.focus(); };
    setTimeout(() => scrim.querySelector('#su-go').focus(), 0);
    const done = (ok) => { close(); resolve(ok); };
    content.querySelector('#su-cancel').addEventListener('click', () => done(false));
    scrim.addEventListener('mousedown', (ev) => { if (ev.target === scrim) done(false); });
    content.querySelector('#su-go').addEventListener('click', async () => {
      const err = content.querySelector('#su-err');
      const status = content.querySelector('#su-status');
      const btn = content.querySelector('#su-go');
      btn.disabled = true; btn.textContent = 'Unlocking…'; err.textContent = '';
      try {
        await runCeremony(status);
        done(true);
      } catch (e) {
        btn.disabled = false; btn.textContent = 'Try again';
        status.textContent = '';
        err.textContent = (e && (e.message || e.code)) || 'Verification failed — try again.';
      }
    });
  });
}
